import type { ReactNode } from 'react'

import { SidebarProvider } from '@components/atoms/Sidebar'
import { GroupSwitcher, type GroupSwitcherProps } from '@molecules/GroupSwitcher'

import type { ComponentDoc, PropDocs } from '../types'

const props: PropDocs<GroupSwitcherProps> = {
  current: {
    type: '{ id: string; name: string } | null',
    required: true,
    description: 'The group the sidebar is scoped to. null means the organisation view.',
  },
  groups: {
    type: 'GroupSwitcherOption[]',
    required: true,
    description: 'Every group the user may switch to: id, name and the href of its overview page.',
  },
  all: {
    type: '{ label: string; href: string }',
    required: false,
    description:
      'The organisation view option ("Home"). Its href is the groups index. The presenter sends it to admins and leaders of more than one group.',
  },
  switchable: {
    type: 'boolean',
    required: true,
    description: 'false renders the current name as a static label with no menu, for a leader of a single group.',
  },
  linkComponent: {
    type: 'ElementType<{ href: string }>',
    default: "'a'",
    description: "Renders each option. Sidebar passes its own down, so Inertia pages get Inertia's Link.",
  },
}

const groups = [
  { id: '1', name: 'Northside', href: '#northside' },
  { id: '2', name: 'Southside', href: '#southside' },
  { id: '3', name: 'West End', href: '#west-end' },
]

const twelveGroups = Array.from({ length: 12 }, (_, i) => ({ id: `${i}`, name: `Group ${i + 1}`, href: `#group-${i}` }))

const current = { id: groups[0].id, name: groups[0].name }

const all = { label: 'Home', href: '#all' }

const frame = (children: ReactNode) => (
  <SidebarProvider className="tw:min-h-0">
    <div className="tw:w-64 tw:rounded-lg tw:bg-gray-900 tw:p-2 tw:text-white tw:[--sidebar-foreground:#fff] tw:[--sidebar-primary:#fff] tw:[--sidebar-primary-foreground:#111827] tw:[--sidebar-accent:rgba(255,255,255,0.16)] tw:[--sidebar-ring:#fff]">
      {children}
    </div>
  </SidebarProvider>
)

const doc: ComponentDoc<GroupSwitcherProps> = {
  slug: 'group-switcher',
  name: 'GroupSwitcher',
  tier: 'molecule',
  description:
    'The scope control at the top of the sidebar. Picking a group navigates to its overview and every sidebar section below re-scopes to it; Home restores the organisation view. It reads the sidebar colour tokens so it sits on any client theme.',
  importStatement: "import { GroupSwitcher } from '@molecules/GroupSwitcher'",
  sourcePath: 'app/frontend/components/molecules/GroupSwitcher.tsx',
  component: GroupSwitcher,
  props,
  examples: [
    {
      title: 'Scoped to a group',
      render: () => frame(<GroupSwitcher current={current} groups={groups} all={all} switchable />),
      code: `<GroupSwitcher
  current={{ id: '1', name: 'Northside' }}
  groups={groups}
  all={{ label: 'Home', href: '/groups' }}
  switchable
/>`,
    },
    {
      title: 'Home',
      render: () => frame(<GroupSwitcher current={null} groups={groups} all={all} switchable />),
      code: `<GroupSwitcher current={null} groups={groups} all={all} switchable />`,
    },
    {
      title: 'More than ten groups',
      note: 'A search box appears above the list and takes focus when the menu opens; the list scrolls inside a fixed height so the search stays put.',
      render: () => frame(<GroupSwitcher current={null} groups={twelveGroups} all={all} switchable />),
      code: `<GroupSwitcher current={null} groups={twelveGroups} all={all} switchable />`,
    },
    {
      title: 'Single-group leader',
      note: 'No menu. The name is a label, so the sidebar reads the same for everyone without offering a choice that has one answer.',
      render: () => frame(<GroupSwitcher current={current} groups={[groups[0]]} all={all} switchable={false} />),
      code: `<GroupSwitcher current={group} groups={[group]} all={all} switchable={false} />`,
    },
  ],
  usage: {
    do: [
      'Mount it once, in the sidebar header, inside SidebarProvider: it is a SidebarMenuButton, so it collapses to the group initials with a tooltip like every other sidebar item. The server decides current, groups and switchable from the group in the URL and the user’s groups: a group page shows that group, any other page shows the organisation view. Admins and leaders of more than one group get the organisation option; a leader of one group gets no switcher. Nothing is remembered between requests.',
      'Keep the options as links. Switching is a navigation, so the URL stays the source of truth and new-tab works.',
    ],
    dont: [
      "Don't use it as a form field. For filtering a list by group use MultiSelectField.",
      "Don't render it when the user has no groups. The presenter omits scope in that case.",
    ],
  },
  a11y: [
    'The trigger is labelled "Change view: <name>", so both the control and the current scope are announced without opening the menu.',
    'Options are real links inside a menu, reachable by arrow keys and Enter. The current group is marked with a check and aria-current.',
    'With more than ten groups the search box is labelled "Search groups", opens focused, and ArrowDown moves from it to the first group.',
    'The static single-group label is plain text, not a disabled control, so it is not announced as something to operate.',
  ],
}

export default doc
